import { type CurrencyConstructor } from '@webapp/core/entities/external/Currency'
import {
  ExternalBankAccountTypes,
  type ExternalBankAccountTypeEnum,
} from '../enums/ExternalBankAccountTypeEnum'

/** Other, EFT and Wire accounts carry only a description */
export interface ExternalBankAccountOtherData {
  description?: string
}

export interface ExternalBankAccountCheckData {
  startNumber: string
}

export interface ExternalBankAccountACHData {
  bankName: string
  bankRoutingNumber: string
  companyName: string
  companyRoutingNumber: string
  companyTaxId: string
}

export type ExternalBankAccountData =
  | ExternalBankAccountOtherData
  | ExternalBankAccountCheckData
  | ExternalBankAccountACHData

interface BaseExternalBankAccountConstructor {
  id: number
  name: string
  currency: CurrencyConstructor
  glCode: string
}

export interface ExternalBankAccountOtherConstructor extends BaseExternalBankAccountConstructor {
  type: typeof ExternalBankAccountTypes.OTHER
  data: ExternalBankAccountOtherData
}

export interface ExternalBankAccountCheckConstructor extends BaseExternalBankAccountConstructor {
  type: typeof ExternalBankAccountTypes.CHECK
  data: ExternalBankAccountCheckData
}

export interface ExternalBankAccountACHConstructor extends BaseExternalBankAccountConstructor {
  type: typeof ExternalBankAccountTypes.ACH
  data: ExternalBankAccountACHData
}

export interface ExternalBankAccountEFTConstructor extends BaseExternalBankAccountConstructor {
  type: typeof ExternalBankAccountTypes.EFT
  data: ExternalBankAccountOtherData
}

export interface ExternalBankAccountWireConstructor extends BaseExternalBankAccountConstructor {
  type: typeof ExternalBankAccountTypes.WIRE
  data: ExternalBankAccountOtherData
}

export type ExternalBankAccountConstructor =
  | ExternalBankAccountOtherConstructor
  | ExternalBankAccountCheckConstructor
  | ExternalBankAccountACHConstructor
  | ExternalBankAccountEFTConstructor
  | ExternalBankAccountWireConstructor

export class ExternalBankAccount {
  readonly id: number
  readonly name: string
  readonly type: ExternalBankAccountTypeEnum
  readonly currency: CurrencyConstructor
  readonly glCode: string
  readonly data: ExternalBankAccountData

  constructor(data: ExternalBankAccountConstructor) {
    this.id = data.id
    this.name = data.name
    this.type = data.type
    this.currency = data.currency
    this.glCode = data.glCode
    this.data = data.data
  }

  isOther(): this is ExternalBankAccount & {
    type: typeof ExternalBankAccountTypes.OTHER
    data: ExternalBankAccountOtherData
  } {
    return this.type === ExternalBankAccountTypes.OTHER
  }

  isCheck(): this is ExternalBankAccount & {
    type: typeof ExternalBankAccountTypes.CHECK
    data: ExternalBankAccountCheckData
  } {
    return this.type === ExternalBankAccountTypes.CHECK
  }

  isACH(): this is ExternalBankAccount & {
    type: typeof ExternalBankAccountTypes.ACH
    data: ExternalBankAccountACHData
  } {
    return this.type === ExternalBankAccountTypes.ACH
  }

  hasDescriptionData(): this is ExternalBankAccount & {
    data: ExternalBankAccountOtherData
  } {
    return (
      this.type === ExternalBankAccountTypes.OTHER ||
      this.type === ExternalBankAccountTypes.EFT ||
      this.type === ExternalBankAccountTypes.WIRE
    )
  }
}
